import { useEffect, useRef, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { useSession, type Identity } from "./auth";
import {
  clearSnapshot,
  canKeepData,
  createDashboardClient,
  restoreSnapshot,
  saveSnapshot,
} from "./dashboardCache";

function AccountQueries({
  identity,
  children,
}: {
  identity: Identity | null;
  children: ReactNode;
}): ReactNode {
  const active = useRef(false);
  const [client] = useState(() => {
    const next = createDashboardClient();
    try {
      if (identity) restoreSnapshot(next, sessionStorage, identity);
    } catch {
      /* Accessing sessionStorage itself can throw. */
    }
    return next;
  });
  useEffect(() => {
    active.current = true;
    if (!identity) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let idle: number | undefined;
    const cancel = () => {
      clearTimeout(timer); timer = undefined;
      if (idle !== undefined) window.cancelIdleCallback(idle);
      idle = undefined;
    };
    const save = () => {
      cancel();
      try {
        saveSnapshot(client, sessionStorage, identity);
      } catch {
        /* Storage unavailable. */
      }
    };
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      // Starting/cancelling a fetch or invalidating an unchanged answer does
      // not change the saved data. Avoid serializing on tab clicks themselves.
      if (event.type !== "updated" || !["success", "error"].includes(event.action.type)) return;
      // Revoked access must leave persisted storage immediately, not wait for idle.
      if (event.action.type === "error" && !canKeepData(event.action.error)) {
        save();
        return;
      }
      if (timer !== undefined || idle !== undefined) return;
      timer = setTimeout(() => {
        timer = undefined;
        if (typeof window.requestIdleCallback === "function") idle = window.requestIdleCallback(save, { timeout: 2000 });
        else timer = setTimeout(save, 0);
      }, 500);
    });
    window.addEventListener("pagehide", save);
    return () => {
      unsubscribe();
      cancel();
      window.removeEventListener("pagehide", save);
      // Never write the old account's snapshot during an account change.
      active.current = false;
      queueMicrotask(() => {
        // StrictMode re-runs effects on the same mounted provider.
        if (!active.current) {
          void client.cancelQueries();
          client.clear();
        }
      });
    };
  }, [client, identity]);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

export function DashboardQueries({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const { identity, status } = useSession();
  useEffect(() => {
    if (status === "signed-out") {
      try {
        clearSnapshot(sessionStorage);
      } catch {
        /* Storage unavailable. */
      }
    }
  }, [status]);
  const verified = status === "signed-in" ? identity : null;
  // A role/account change discards observers as well as the entire old cache.
  return (
    <AccountQueries
      key={verified ? `${verified.email}|${verified.role}` : status}
      identity={verified}
    >
      {children}
    </AccountQueries>
  );
}
